import React, { useState, useEffect } from 'react';
import { VStack, HStack, Text, Button, NativeSelect } from '@chakra-ui/react';
import Drawer, { DrawerHeader, DrawerTitle, DrawerBody, DrawerFooter, DrawerCloseTrigger } from '@/components/ui/Drawer';
import { Organization, useGetConfigQuery, useUpdateConfigMutation } from '../api';
import LoadingSkeleton from '@/components/LoadingSkeleton';

interface EditConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  organization: Organization | null;
}

const EditConfigurationModal: React.FC<EditConfigurationModalProps> = ({
  isOpen,
  onClose,
  organization,
}) => {
  const [showConditionGroups, setShowConditionGroups] = useState<boolean | null>(null);
  const [originalShowConditionGroups, setOriginalShowConditionGroups] = useState<boolean | null>(null);
  const [showConditionSearch, setShowConditionSearch] = useState<boolean | null>(null);
  const [originalShowConditionSearch, setOriginalShowConditionSearch] = useState<boolean | null>(null);
  const [redirectSpeedbumpToConditionPage, setRedirectSpeedbumpToConditionPage] = useState<boolean | null>(null);
  const [originalRedirectSpeedbumpToConditionPage, setOriginalRedirectSpeedbumpToConditionPage] = useState<boolean | null>(null);
  const [showAssistantDoctorPicker, setShowAssistantDoctorPicker] = useState<boolean | null>(null);
  const [originalShowAssistantDoctorPicker, setOriginalShowAssistantDoctorPicker] = useState<boolean | null>(null);
  const [showEnrolments, setShowEnrolments] = useState<boolean | null>(null);
  const [enableDashboardChat, setEnableDashboardChat] = useState<boolean | null>(null);
  const [originalShowEnrolments, setOriginalShowEnrolments] = useState<boolean | null>(null);
  const [originalEnableDashboardChat, setOriginalEnableDashboardChat] = useState<boolean | null>(null);
  const [optOutEnrollmentConsent, setOptOutEnrollmentConsent] = useState<boolean | null>(null);
  const [originalOptOutEnrollmentConsent, setOriginalOptOutEnrollmentConsent] = useState<boolean | null>(null);
  const [error, setError] = useState('');

  // Fetch config when modal opens and organization is selected
  const { data: config, isFetching: isLoadingConfig } = useGetConfigQuery(organization?.id || 0, {
    skip: !organization?.id || !isOpen,
    refetchOnMountOrArgChange: true,
  });

  const [updateConfig, { isLoading: isUpdating }] = useUpdateConfigMutation();

  // Initialize form when config loads
  useEffect(() => {
    if (config) {
      const initialConditionGroups = config.show_condition_groups ?? null;
      const initialConditionSearch = config.show_condition_search ?? null;
      const initialRedirectSpeedbumpToConditionPage = config.redirect_speedbump_to_condition_page ?? null;
      const initialAssistantDoctorPicker = config.show_assistant_doctor_picker ?? null;
      const initialShowEnrolments = config.show_enrolments ?? null;
      const initialEnableDashboardChat = config.enable_dashboard_chat ?? null;
      const initialOptOutEnrollmentConsent = config.opt_out_enrollment_consent ?? null;
      setShowConditionGroups(initialConditionGroups);
      setOriginalShowConditionGroups(initialConditionGroups);
      setShowConditionSearch(initialConditionSearch);
      setOriginalShowConditionSearch(initialConditionSearch);
      setRedirectSpeedbumpToConditionPage(initialRedirectSpeedbumpToConditionPage);
      setOriginalRedirectSpeedbumpToConditionPage(initialRedirectSpeedbumpToConditionPage);
      setShowAssistantDoctorPicker(initialAssistantDoctorPicker);
      setOriginalShowAssistantDoctorPicker(initialAssistantDoctorPicker);
      setShowEnrolments(initialShowEnrolments);
      setEnableDashboardChat(initialEnableDashboardChat);
      setOriginalShowEnrolments(initialShowEnrolments);
      setOriginalEnableDashboardChat(initialEnableDashboardChat);
      setOptOutEnrollmentConsent(initialOptOutEnrollmentConsent);
      setOriginalOptOutEnrollmentConsent(initialOptOutEnrollmentConsent);
    }
  }, [config]);

  const handleSubmit = async () => {
    if (!organization) return;

    setError('');

    // Check if anything changed
    const hasChanges =
      showConditionGroups !== originalShowConditionGroups ||
      showConditionSearch !== originalShowConditionSearch ||
      redirectSpeedbumpToConditionPage !== originalRedirectSpeedbumpToConditionPage ||
      showAssistantDoctorPicker !== originalShowAssistantDoctorPicker ||
      enableDashboardChat !== originalEnableDashboardChat ||
      showEnrolments !== originalShowEnrolments ||
      optOutEnrollmentConsent !== originalOptOutEnrollmentConsent;

    // If nothing changed, just close
    if (!hasChanges) {
      onClose();
      return;
    }

    // Build update request with only changed fields
    const updateRequest: { id: number } & Record<string, number | boolean | null> = {
      id: organization.id,
    };

    if (showConditionGroups !== originalShowConditionGroups) {
      updateRequest.show_condition_groups = showConditionGroups;
    }
    if (showConditionSearch !== originalShowConditionSearch) {
      updateRequest.show_condition_search = showConditionSearch;
    }
    if (redirectSpeedbumpToConditionPage !== originalRedirectSpeedbumpToConditionPage) {
      updateRequest.redirect_speedbump_to_condition_page = redirectSpeedbumpToConditionPage;
    }
    if (showAssistantDoctorPicker !== originalShowAssistantDoctorPicker) {
      updateRequest.show_assistant_doctor_picker = showAssistantDoctorPicker;
    }
    if (showEnrolments !== originalShowEnrolments) {
      updateRequest.show_enrolments = showEnrolments;
    }
    if (enableDashboardChat !== originalEnableDashboardChat) {
      updateRequest.enable_dashboard_chat = enableDashboardChat;
    }
    if (optOutEnrollmentConsent !== originalOptOutEnrollmentConsent) {
      updateRequest.opt_out_enrollment_consent = optOutEnrollmentConsent;
    }

    try {
      await updateConfig(updateRequest).unwrap();

      onClose();
    } catch (err: any) {
      setError(err.data?.message || 'Failed to update configuration');
    }
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  const isLoading = isLoadingConfig || isUpdating;

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} size="md">
      <DrawerHeader>
        <DrawerTitle>Change Settings</DrawerTitle>
        <DrawerCloseTrigger />
      </DrawerHeader>

      <DrawerBody>
        {isLoadingConfig ? (
          <LoadingSkeleton rows={2} />
        ) : (
          <VStack gap={4} align="stretch">
            {organization && (
              <Text fontSize="sm" color="gray.600">
                Editing organization settings for: <strong>{organization.name}</strong>
              </Text>
            )}

            <div>
              <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>
                Show Condition Groups
              </Text>
              <NativeSelect.Root>
                <NativeSelect.Field
                  aria-label="Show condition groups"
                  value={showConditionGroups === null ? 'inherit' : String(showConditionGroups)}
                  onChange={(event) => setShowConditionGroups(event.target.value === 'inherit' ? null : event.target.value === 'true')}
                >
                  <option value="inherit">Use system default</option>
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Controls whether condition groups are displayed for this organization's users
              </Text>
            </div>

            <div>
              <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>
                Show Condition Search
              </Text>
              <NativeSelect.Root>
                <NativeSelect.Field
                  aria-label="Show condition search"
                  value={showConditionSearch === null ? 'inherit' : String(showConditionSearch)}
                  onChange={(event) => setShowConditionSearch(event.target.value === 'inherit' ? null : event.target.value === 'true')}
                >
                  <option value="inherit">Use system default</option>
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Controls whether condition group search is displayed for this organization's users
              </Text>
            </div>

            <div>
              <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>
                Route Patients Directly to Condition Page
              </Text>
              <NativeSelect.Root>
                <NativeSelect.Field
                  aria-label="Redirect speedbump to condition page"
                  value={redirectSpeedbumpToConditionPage === null ? 'inherit' : String(redirectSpeedbumpToConditionPage)}
                  onChange={(event) => setRedirectSpeedbumpToConditionPage(event.target.value === 'inherit' ? null : event.target.value === 'true')}
                >
                  <option value="inherit">Use system default</option>
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Removes the intermediate selection screen for a faster, one-tap experience. Disable to keep the privacy buffer where patients self-select their condition.
              </Text>
            </div>

            <div>
              <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>
                Show Assistant Doctor Picker
              </Text >
              <NativeSelect.Root>
                <NativeSelect.Field
                  aria-label="Show assistant doctor picker"
                  value={showAssistantDoctorPicker === null ? 'inherit' : String(showAssistantDoctorPicker)}
                  onChange={(event) => setShowAssistantDoctorPicker(event.target.value === 'inherit' ? null : event.target.value === 'true')}
                >
                  <option value="inherit">Use system default</option>
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Controls whether assistants can switch doctors in the send drawer
              </Text>
            </div >

            <div>
              <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>
                Show Enrollments
              </Text>
              <NativeSelect.Root>
                <NativeSelect.Field
                  aria-label="Show enrolments"
                  value={showEnrolments === null ? 'inherit' : String(showEnrolments)}
                  onChange={(event) => setShowEnrolments(event.target.value === 'inherit' ? null : event.target.value === 'true')}
                >
                  <option value="inherit">Use system default</option>
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Controls whether this organization's users can access enrollments
              </Text>
            </div >

            <div>
              <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>
                Enable Dashboard Chat
              </Text>
              <NativeSelect.Root>
                <NativeSelect.Field
                  aria-label="Enable dashboard chat"
                  value={enableDashboardChat === null ? 'inherit' : String(enableDashboardChat)}
                  onChange={(event) => setEnableDashboardChat(event.target.value === 'inherit' ? null : event.target.value === 'true')}
                >
                  <option value="inherit">Use system default</option>
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Enables AI chat for this doctor context, including assigned assistants. The global chat switch must also be enabled.
              </Text>
            </div >

            <div>
              <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>
                Opt Out Enrollment Consent
              </Text>
              <NativeSelect.Root>
                <NativeSelect.Field
                  aria-label="Opt out enrollment consent"
                  value={optOutEnrollmentConsent === null ? 'inherit' : String(optOutEnrollmentConsent)}
                  onChange={(event) => setOptOutEnrollmentConsent(event.target.value === 'inherit' ? null : event.target.value === 'true')}
                >
                  <option value="inherit">Use system default</option>
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Skips the in-app communication consent step when creating enrollments for this organization.
              </Text>
            </div >

            {error && (
              <Text color="red.500" fontSize="sm">
                {error}
              </Text>
            )}
          </VStack >
        )}
      </DrawerBody >

      <DrawerFooter>
        <HStack gap={3}>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={isUpdating}
            disabled={isLoading}
            colorPalette="cyan"
          >
            Save Configuration
          </Button>
        </HStack>
      </DrawerFooter>
    </Drawer >
  );
};

export default EditConfigurationModal;
